import {
  Box,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
} from "@mui/material";
import React from "react";
import { useParams } from "react-router-dom";
import { API_URL, downloadFile, IMAGES_API_URL } from "../constants";
import { useMutation, gql, useQuery } from "@apollo/client";
import { PDFDocument, StandardFonts, rgb, degrees } from "pdf-lib";
import { QRCodeCanvas } from "@cheprasov/qrcode";
import { fetch } from "cross-fetch";

export default function RoleCertificatePage() {
  const { eventId, role } = useParams();
  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ textTransform: "capitalize" }}
      >
        {role} Certificate
      </Typography>

      <Container maxWidth="xl">
        <CertificateBuilder eventId={eventId} role={role} />
      </Container>
      <Box p={4} />
    </Box>
  );
}

function CertificateBuilder({ eventId, role }) {
  const [doc, setDoc] = React.useState(null);

  const { data: certificateData, refetch } = useQuery(
    gql`
      query ($eventId: String!, $role: ROLE!) {
        certificate(eventId: $eventId, role: $role)
      }
    `,
    { variables: { eventId, role }, onCompleted: (e) => console.log(e) }
  );

  const [upload, { loading, error }] = useMutation(gql`
    mutation ($file: Upload!, $eventId: String!, $role: ROLE!) {
      uploadEventRoleCertificate(eventId: $eventId, role: $role, file: $file)
    }
  `);

  const [deleteCertificate] = useMutation(gql`
    mutation ($eventId: String!, $role: ROLE!) {
      deleteEventRoleCertificate(eventId: $eventId, role: $role)
    }
  `);

  const [saveCertificate] = useMutation(gql`
    mutation ($eventId: String!, $role: ROLE!, $certificate: String!) {
      saveCertificate(eventId: $eventId, role: $role, certificate: $certificate)
    }
  `);

  function onChange({
    target: {
      validity,
      files: [file],
    },
  }) {
    if (validity.valid)
      upload({ variables: { eventId, role, file } }).then(onLoadDoc);
  }

  const onLoadDoc = async () => {
    try {
      const url = API_URL + `download-certificate/${eventId}/${role}`;
      const existingPdfBytes = await fetch(url).then((res) =>
        res.arrayBuffer()
      );
      setDoc(await PDFDocument.load(existingPdfBytes));
    } catch (e) {
      console.log(e);
    }
  };

  const handleSaveElements = (certificate) => {
    // console.log(certificate);
    return saveCertificate({
      variables: {
        eventId,
        role,
        certificate,
      },
    })
      .then((res) => alert(res.data.saveCertificate))
      .catch((e) => alert(JSON.stringify(e, null, 2)));
  };

  const handleExportDesign = async (design) => {
    // console.log(design);
    downloadFile(
      document,
      JSON.stringify(design),
      `${eventId}_${role}_design.json`
    );
  };

  const handleImportDesign = async (e) => {
    const [file] = e.target.files;
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      handleSaveElements(reader.result).then(() => refetch());
    };
    reader.readAsText(file);
  };

  React.useEffect(() => {
    const t = setTimeout(onLoadDoc, 1000);
    return () => {
      clearTimeout(t);
    };
  }, [eventId, role]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <label htmlFor="pdf">Select Certificate(PDF) to Import: </label>
          <input
            type="file"
            accept="application/pdf"
            id="pdf"
            name="pdf"
            required
            onChange={onChange}
          />
          <label htmlFor="design">Select Design(JSON) to Import: </label>
          <input
            type="file"
            accept="application/json"
            id="design"
            name="design"
            required
            onChange={handleImportDesign}
          />
          <Box>
            <Button
              onClick={() =>
                deleteCertificate({ variables: { eventId, role } })
              }
              color="error"
            >
              Remove PDF
            </Button>
            <Button
              onClick={() => handleSaveElements("[]").then(() => refetch())}
              color="error"
            >
              Remove Design
            </Button>
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        {!doc ? (
          "Upload Certificate PDF"
        ) : (
          <RoleCertificate
            doc={doc}
            onSave={handleSaveElements}
            onExportDesign={handleExportDesign}
            certificate={certificateData?.certificate}
          />
        )}
      </Grid>
    </Grid>
  );
}

function RoleCertificate({ doc, onSave, certificate, onExportDesign }) {
  const [elements, setElements] = React.useState(
    !certificate ? [] : JSON.parse(certificate)
  );
  const pdfRef = React.useRef(null);
  React.useEffect(() => {
    (async () => {
      if (doc) {
        pdfRef.current.src =
          (await modifyPdf(doc, elements)) + "#page=1&zoom=100";
      }
    })();
  }, [elements]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <Stack spacing={1}>
          {elements.map((el, i) => (
            <TextField
              key={i}
              defaultValue={el}
              label="Element Config"
              fullWidth
              multiline
              maxRows={30}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === "Enter") {
                  const v = e.target.value;
                  if (!v) {
                    console.log("removing", i);
                    setElements(elements.filter((_, k) => i !== k));
                  } else {
                    setElements(
                      elements.map((a, k) => {
                        return JSON.parse(a).id === JSON.parse(v).id ? v : a;
                      })
                    );
                  }
                }
              }}
            />
          ))}
          <Button
            fullWidth
            size="large"
            color="success"
            variant="contained"
            onClick={() =>
              setElements([
                ...elements,
                JSON.stringify(
                  {
                    id: Date.now(),
                    type: "text",
                    value: "$First Name",
                    bold: false,
                    size: 16,
                    bound: {
                      x: 10,
                      y: 600,
                      width: 100,
                      height: 16,
                    },
                  },
                  null,
                  2
                ),
              ])
            }
          >
            Add More Element
          </Button>
          <Button
            onClick={() => onSave(JSON.stringify(elements))}
            color="error"
            fullWidth
            variant="contained"
            size="large"
          >
            Save Certificate
          </Button>
          <Button
            onClick={() => onExportDesign(elements)}
            color="warning"
            fullWidth
            variant="contained"
            size="large"
          >
            Export Design
          </Button>
        </Stack>
      </Grid>
      <Grid item xs={12} md={8} container spacing={1}>
        <Grid item xs={12}>
          {/* <iframe ref={pdfRef} style={{ width: "100%", height: 400 }}></iframe>; */}
          <embed
            ref={pdfRef}
            width="100%"
            height={842}
            type="application/pdf"
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

async function modifyPdf(docOriginal, elements) {
  const doc = await docOriginal.copy();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);

  const pages = doc.getPages();
  const page = pages[0];
  const { width, height } = page.getSize();
  page.resetPosition();

  const drawText = (
    text,
    {
      size = 16,
      bold = false,
      bounds = { x: 100, y: 100, width: 100, height: 20 },
    } = {}
  ) => {
    let textWidth = (bold ? fontBold : font).widthOfTextAtSize(text, size);
    if (textWidth > bounds.width) {
      size *= 0.8;
      textWidth = (bold ? fontBold : font).widthOfTextAtSize(text, size);
    }

    page.moveTo(
      bounds.x + (bounds.width - textWidth) / 2,
      bounds.y + bounds.height - size
    );

    page.drawText(text, {
      size,
      font: bold ? fontBold : font,
    });
  };

  if (elements?.length) {
    for (const str of elements) {
      const el = JSON.parse(str);
      // page.drawRectangle({
      //   ...el.bound,
      //   borderColor: rgb(1, 0, 0),
      //   borderWidth: 1,
      // });
      if (el.type === "text") {
        if (
          font.widthOfTextAtSize(el.value, 18) > el.bound.width &&
          el.value.indexOf("||") > -1
        ) {
          drawText(el.value.split("||")[0].trim(), {
            bold: el.bold,
            bounds: el.bound,
            size: el.size,
          });
          drawText(el.value.split("||")[1].trim(), {
            bold: el.bold,
            bounds: { ...el.bound, y: el.bound.y - el.size - el.size * 0.25 },
            size: el.size,
          });
        } else if (font.widthOfTextAtSize(el.value, 18) > el.bound.width) {
          drawText(el.value.replace("||", " "), {
            bold: el.bold,
            size: Math.floor(el.size * 0.7),
            bounds: el.bound,
          });
        } else {
          drawText(el.value.replace("||", " "), {
            bold: el.bold,
            size: el.size, // Math.floor(el.size * 0.7),
            bounds: el.bound,
          });
        }
      } else if (el.type === "qr") {
        const qr = new QRCodeCanvas(el.value);
        const qrImg = await doc.embedPng(qr.toDataUrl());
        const qrSize = el.size;
        page.drawImage(qrImg, {
          x: el.bound.x + (el.bound.width - qrSize) / 2,
          y: el.bound.y,
          width: qrSize,
          height: qrSize,
        });
      }
    }
  }
  // console.log("should display pdf");

  const bytes = await doc.save();
  const blob = new Blob([bytes], { type: "application/pdf" });

  return URL.createObjectURL(blob);
}
